'use client';

import React, { ReactNode, useState } from 'react';

import { markdownToComponent } from '@/utils/convertMarkdown';
import { getManagedDatagridSetters } from '@/utils/hooks/useManagedDatagrid';
import { cn } from '@/utils/lib/utils';
//Icons and icon key
import { ChevronDown } from '@/utils/lucide';
import { ChevronUp } from '@/utils/lucide';
import { ChevronsDownUp } from '@/utils/lucide';
import truncateMarkdown from '@/utils/truncateMarkdown';

import { addFieldState, setFieldState } from '@/redux/interactionSlice';
import store, { RootState } from '@/redux/store';

import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { TextCellOptions, TextCellProps } from './columns';

const TextCell = ({ value, row, column, cell, md, truncate, truncateOffset, children, table }: TextCellProps & TextCellOptions & { children?: ReactNode }) =>
{
	const key = `${row?.original?.id}-${column?.columnDef?.accessorKey}`;
	const initialOpen = store.getState().interaction.fieldState[key] ?? false;
	const [open, setOpen] = useState(initialOpen);

	const shortAnswers = table.options.meta?.shortAnswers;
	const columnWidth = column.getSize();
	// Approximate characters per line based on column width (assuming average char width of 8px)
	const charsPerLine = Math.floor(columnWidth / 8);
	const twoLineCharLimit = charsPerLine * 2;

	// console.log('table', table);

	if (value === undefined || value === null)
	{
		return (
			<div>
				<p className='text-sm text-gray-400'>No Value Available</p>
			</div>
		);
	}

	//Convert to string if it's an object
	if (typeof value === 'object')
	{
		value = value.value;
	}

	if (!value)
	{
		return (
			<div>
				<p className='text-sm text-gray-400'>No Value Available</p>
			</div>
		);
	}

	let displayText = '';
	let displayComponent = <></>;
	let isTruncated = false;

	if (shortAnswers)
	{
		// In short answers mode, always truncate to two lines worth of characters
		displayText = truncateMarkdown(value, twoLineCharLimit, true);
		isTruncated = displayText !== value;
		displayComponent = md ? markdownToComponent(displayText) : <p>{displayText}</p>;
	}
	else if (truncate)
	{
		if (!truncateOffset)
		{
			truncateOffset = Math.ceil(truncate / 8);
		}

		if (md)
		{
			if (value.length < truncate + truncateOffset)
			{
				displayText = value;
				displayComponent = markdownToComponent(displayText);
			}
			else
			{
				displayText = open ? value : truncateMarkdown(value, truncate, true);
				isTruncated = !open && displayText !== value;
				displayComponent = markdownToComponent(displayText);
			}
		}
		else
		{
			if (value.length < truncate + truncateOffset)
			{
				displayText = value;
				displayComponent = <p>{displayText}</p>;
			}
			else
			{
				displayText = open ? value : value.slice(0, truncate);
				isTruncated = !open && displayText !== value;
				displayComponent = <p>{displayText}</p>;
			}
		}
	}
	else
	{
		displayText = value;
		displayComponent = md ? markdownToComponent(displayText) : <p>{displayText}</p>;
	}

	return (
		<div className='flex flex-col'>
			<div className=''>
				{displayComponent}
				{shortAnswers && isTruncated && (
					<div
						className='absolute -bottom-0.5 -right-0.5 text-gray-300 cursor-pointer'
						onClick={e =>
						{
							const id = table.options.meta?.identifier;
							const { setExpanded } = getManagedDatagridSetters(id);
							setExpanded(true);
						}}
					>
						<ChevronsDownUp className='text-icon-sm' />
					</div>
				)}
			</div>

			<div className='flex flex-row w-full justify-center items-center pl-2'>
				{!shortAnswers && truncate && value.length > truncate + truncateOffset! && (
					<button
						className='flex flex-row items-center justify-center px-0 h-8 text-nowrap text-xs text-gray-400 hover:text-gray-700'
						onClick={e =>
						{
							console.log('Toggling open', open);
							store.dispatch(
								addFieldState({
									key,
									value: !open
								})
							);
							setOpen(!open);
							e.stopPropagation();
						}}
					>
						{open ? (
							<p className='flex flex-row items-center'>
								Collapse Text
								<span className='text-icon-sm pl-2'>
									<ChevronUp />
								</span>
							</p>
						) : (
							<p className='flex flex-row items-center'>
								Show Full Text
								<span className='text-icon-sm pl-2'>
									<ChevronDown />
								</span>
							</p>
						)}
					</button>
				)}
				{children}
			</div>
		</div>
	);
};

export default TextCell;
